import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useAuth } from 'react-oidc-context';
import GenericButton from '../components/molecules/GenericButton';
import WalletIcon from '../components/Icons/WalletIcon';
import WalletRequestConfirmModal from '../components/modal/WalletRequestConfirmModal';
import WalletRequestLoadingModal from '../components/modal/WalletRequestLoadingModal';
import WalletRequestSuccessModal from '../components/modal/WalletRequestSuccessModal';
import { useTractionService } from '../services';
import { IUserReservation } from '../types/reservation';
import { useToast } from '../contexts';
import {
  getResponsivePadding,
  getResponsiveFontSize,
  getResponsiveContainerWidth
} from '../helpers/utils';

/**
 * FallbackPage component - shown to users who are do not have a wallet yet
 *
 * @return {*}
 */
const FallbackPage: React.FC = () => {
  const authContext = useAuth();
  const tractionService = useTractionService();
  const { showToast } = useToast();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [walletReservation, setWalletReservation] = useState<{
    reservation: IUserReservation;
    onboarding_url: string;
  } | null>(null);

  const handleRequestWallet = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirmWalletRequest = async () => {
    setShowConfirmationModal(false);
    setIsLoading(true);

    try {
      const userReservation = await tractionService.requestWallet(
        authContext.user?.profile.email || '',
        authContext.user?.profile.name || ''
      );

      if (userReservation) {
        setWalletReservation(userReservation);
        setShowSuccessModal(true);
        showToast('Wallet request successful', 5000, true);
      }
    } catch (error) {
      setIsLoading(false);
      setShowConfirmationModal(false);
      setShowSuccessModal(false);
      setWalletReservation(null);

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showToast(`Error requesting wallet: ${errorMessage}`, 5000, false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectWalletRequest = () => {
    setShowConfirmationModal(false);
  };

  const handleSuccessModalClose = () => {
    authContext.signinRedirect();
    setShowSuccessModal(false);
    setWalletReservation(null);
  };

  const handleSignOut = () => {
    authContext.signoutRedirect();
  };

  return (
    <>
      <View style={styles.container}>
        <View style={styles.iconContainer}>
          <WalletIcon />
        </View>

        <Text style={styles.title}>Digital Wallet Service</Text>

        <View style={styles.descriptionSection}>
          <Text style={styles.description}>
            Securely store, manage, and share your digital credentials with ease. Our digital wallet
            service provides a trusted way to handle your important documents and certificates in
            the digital world.
          </Text>
        </View>

        <View style={styles.featuresSection}>
          <Text style={styles.featuresTitle}>Key Features:</Text>

          <View style={styles.featureList}>
            <Text style={styles.feature}>🔒 Secure credential storage</Text>
            <Text style={styles.feature}>🎯 Selective information sharing</Text>
            <Text style={styles.feature}>✅ Verified digital documents</Text>
            <Text style={styles.feature}>📱 Mobile-friendly access</Text>
            <Text style={styles.feature}>🔐 Privacy-focused design</Text>
          </View>
        </View>

        <View style={styles.actionSection}>
          <Text style={styles.actionTitle}>Ready to Get Started?</Text>
          <Text style={styles.actionDescription}>
            Request access to your digital wallet to begin storing and managing your credentials
            securely.
          </Text>
        </View>

        <View style={styles.buttonContainer}>
          <GenericButton
            title="Request Wallet"
            onPress={handleRequestWallet}
            isPrimary={true}
            buttonStyle={styles.primaryButton}
          />

          <GenericButton
            title="Sign Out"
            onPress={handleSignOut}
            isPrimary={false}
            buttonStyle={styles.secondaryButton}
          />
        </View>

        <Text style={styles.footerText}>
          Need help with your wallet request? Contact your administrator for assistance.
        </Text>
      </View>

      <WalletRequestConfirmModal
        isVisible={showConfirmationModal}
        user={authContext.user}
        onConfirm={handleConfirmWalletRequest}
        onReject={handleRejectWalletRequest}
      />

      <WalletRequestLoadingModal isVisible={isLoading} />

      <WalletRequestSuccessModal
        isVisible={showSuccessModal}
        walletReservation={walletReservation}
        onClose={handleSuccessModalClose}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: getResponsivePadding(16, 20, 24),
    maxWidth: getResponsiveContainerWidth(),
    alignSelf: 'center',
    width: '100%'
  },
  iconContainer: {
    marginBottom: getResponsivePadding(12, 14, 16)
  },
  title: {
    fontSize: getResponsiveFontSize(24, 28, 32),
    fontWeight: 'bold',
    marginBottom: getResponsivePadding(16, 20, 24),
    color: '#1C274C',
    textAlign: 'center'
  },
  descriptionSection: {
    marginBottom: getResponsivePadding(12, 14, 16),
    width: '100%'
  },
  description: {
    fontSize: getResponsiveFontSize(14, 16, 18),
    lineHeight: getResponsiveFontSize(20, 24, 28),
    color: '#4B5563',
    textAlign: 'center',
    marginBottom: getResponsivePadding(12, 14, 16),
    paddingHorizontal: getResponsivePadding(8, 12, 16)
  },
  featuresSection: {
    marginBottom: getResponsivePadding(12, 14, 16),
    width: '100%'
  },
  featuresTitle: {
    fontSize: getResponsiveFontSize(16, 18, 20),
    fontWeight: '600',
    marginBottom: getResponsivePadding(12, 14, 16),
    color: '#1C274C',
    textAlign: 'center'
  },
  featureList: {
    alignItems: 'flex-start',
    alignSelf: 'center',
    width: '100%',
    maxWidth: 400
  },
  feature: {
    fontSize: getResponsiveFontSize(13, 14, 16),
    lineHeight: getResponsiveFontSize(18, 20, 24),
    color: '#4B5563',
    marginBottom: getResponsivePadding(6, 7, 8),
    textAlign: 'left',
    paddingHorizontal: getResponsivePadding(8, 12, 16)
  },
  actionSection: {
    marginBottom: getResponsivePadding(24, 28, 32),
    width: '100%'
  },
  actionTitle: {
    fontSize: getResponsiveFontSize(18, 20, 22),
    fontWeight: '600',
    marginBottom: getResponsivePadding(8, 10, 12),
    color: '#1C274C',
    textAlign: 'center'
  },
  actionDescription: {
    fontSize: getResponsiveFontSize(13, 14, 16),
    lineHeight: getResponsiveFontSize(18, 20, 24),
    color: '#4B5563',
    textAlign: 'center',
    paddingHorizontal: getResponsivePadding(8, 12, 16)
  },
  buttonContainer: {
    width: '100%',
    gap: getResponsivePadding(12, 14, 16),
    marginBottom: getResponsivePadding(20, 22, 24),
    maxWidth: 300,
    alignSelf: 'center'
  },
  primaryButton: {
    marginBottom: getResponsivePadding(6, 7, 8)
  },
  secondaryButton: {
    marginBottom: getResponsivePadding(6, 7, 8)
  },
  footerText: {
    fontSize: getResponsiveFontSize(11, 12, 14),
    color: '#6B7280',
    textAlign: 'center',
    fontStyle: 'italic',
    paddingHorizontal: getResponsivePadding(16, 20, 24)
  }
});

export default FallbackPage;
